import type { PatchFileRecordRegionPayload } from "@cohesive/api-client";
import { useCallback, useEffect, useRef } from "react";
import { useHtmlInlineEditController } from "../HtmlInlineEditContext";

export const AUTOSAVE_IDLE_MS = 5_000;

type PatchRegions = (payload: PatchFileRecordRegionPayload) => Promise<unknown>;

export interface UseHtmlIslandAutosaveParams {
  getIframeDoc: () => Document | null;
  patchRegions: PatchRegions;
  onSaveSuccess: () => void;
}

export interface UseHtmlIslandAutosaveResult {
  /** Region ids with local content differing from baseline. */
  getDirtyEditIds: () => ReadonlySet<string>;
  handleIslandFocus: (editId: string, innerHTML: string) => void;
  handleIslandInput: (editId: string, innerHTML: string) => void;
  handleIslandFocusOut: (editId: string, innerHTML: string) => void;
  flushDirtyRegions: () => void;
  /** Clear autosave state; pass `{ flush: true }` to PATCH pending regions first. */
  resetAutosave: (options?: { flush?: boolean }) => void;
}

export function useHtmlIslandAutosave({
  getIframeDoc,
  patchRegions,
  onSaveSuccess,
}: UseHtmlIslandAutosaveParams): UseHtmlIslandAutosaveResult {
  const { setAutosaveState, resetAutosaveState } =
    useHtmlInlineEditController();

  const baselineByIdRef = useRef(new Map<string, string>());
  const dirtyIdsRef = useRef(new Set<string>());
  const saveInFlightRef = useRef(false);
  const flushAfterFlightRef = useRef(false);
  const idleTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const patchRegionsRef = useRef(patchRegions);
  const onSaveSuccessRef = useRef(onSaveSuccess);
  const getIframeDocRef = useRef(getIframeDoc);

  useEffect(() => {
    patchRegionsRef.current = patchRegions;
  }, [patchRegions]);
  useEffect(() => {
    onSaveSuccessRef.current = onSaveSuccess;
  }, [onSaveSuccess]);
  useEffect(() => {
    getIframeDocRef.current = getIframeDoc;
  }, [getIframeDoc]);

  const syncContextState = useCallback(() => {
    setAutosaveState({
      pendingCount: dirtyIdsRef.current.size,
      isSaving: saveInFlightRef.current,
    });
  }, [setAutosaveState]);

  const setRegionDirty = useCallback(
    (editId: string, innerHTML: string) => {
      const baseline = baselineByIdRef.current.get(editId);
      if (baseline === undefined) {
        baselineByIdRef.current.set(editId, innerHTML);
        dirtyIdsRef.current.delete(editId);
      } else if (innerHTML === baseline) {
        dirtyIdsRef.current.delete(editId);
      } else {
        dirtyIdsRef.current.add(editId);
      }
      syncContextState();
    },
    [syncContextState],
  );

  const clearIdleTimer = useCallback(() => {
    if (idleTimerRef.current !== null) {
      clearTimeout(idleTimerRef.current);
      idleTimerRef.current = null;
    }
  }, []);

  const readIslandContent = useCallback((editId: string): string | null => {
    const doc = getIframeDocRef.current();
    if (!doc) return null;
    const el = doc.querySelector<HTMLElement>(`[data-edit-id="${editId}"]`);
    return el?.innerHTML ?? null;
  }, []);

  const collectDirtyRegionsPayload =
    useCallback((): PatchFileRecordRegionPayload["regions"] => {
      const regions: PatchFileRecordRegionPayload["regions"] = [];
      for (const editId of dirtyIdsRef.current) {
        const content = readIslandContent(editId);
        if (content === null) {
          dirtyIdsRef.current.delete(editId);
          continue;
        }
        const baseline = baselineByIdRef.current.get(editId);
        if (baseline === undefined || content === baseline) {
          dirtyIdsRef.current.delete(editId);
          continue;
        }
        regions.push({ region_id: editId, content });
      }
      return regions;
    }, [readIslandContent]);

  const runBatchSave = useCallback(() => {
    if (saveInFlightRef.current) {
      flushAfterFlightRef.current = true;
      return;
    }

    const regions = collectDirtyRegionsPayload();
    if (regions.length === 0) {
      syncContextState();
      return;
    }

    saveInFlightRef.current = true;
    syncContextState();

    void (async () => {
      try {
        await patchRegionsRef.current({ regions });
        for (const { region_id, content } of regions) {
          baselineByIdRef.current.set(region_id, content);
          const currentContent = readIslandContent(region_id);
          if (currentContent !== null && currentContent !== content) {
            // Edits during the in-flight PATCH must stay dirty for a follow-up save.
            dirtyIdsRef.current.add(region_id);
          } else {
            dirtyIdsRef.current.delete(region_id);
          }
        }
        onSaveSuccessRef.current();
      } catch (err) {
        console.error("[HtmlContent] island autosave failed:", err);
      } finally {
        saveInFlightRef.current = false;
        const needsFollowUp = flushAfterFlightRef.current;
        flushAfterFlightRef.current = false;
        if (needsFollowUp) {
          runBatchSave();
        } else {
          syncContextState();
        }
      }
    })();
  }, [collectDirtyRegionsPayload, syncContextState]);

  const flushDirtyRegions = useCallback(() => {
    clearIdleTimer();
    runBatchSave();
  }, [clearIdleTimer, runBatchSave]);

  const scheduleIdleFlush = useCallback(() => {
    clearIdleTimer();
    idleTimerRef.current = setTimeout(() => {
      idleTimerRef.current = null;
      flushDirtyRegions();
    }, AUTOSAVE_IDLE_MS);
  }, [clearIdleTimer, flushDirtyRegions]);

  const handleIslandFocus = useCallback(
    (editId: string, innerHTML: string) => {
      if (!baselineByIdRef.current.has(editId)) {
        baselineByIdRef.current.set(editId, innerHTML);
      }
      setRegionDirty(editId, innerHTML);
    },
    [setRegionDirty],
  );

  const handleIslandInput = useCallback(
    (editId: string, innerHTML: string) => {
      setRegionDirty(editId, innerHTML);
      if (dirtyIdsRef.current.has(editId)) {
        scheduleIdleFlush();
      } else if (dirtyIdsRef.current.size === 0) {
        clearIdleTimer();
      }
    },
    [clearIdleTimer, scheduleIdleFlush, setRegionDirty],
  );

  const handleIslandFocusOut = useCallback(
    (editId: string, innerHTML: string) => {
      setRegionDirty(editId, innerHTML);
    },
    [setRegionDirty],
  );

  const getDirtyEditIds = useCallback(() => new Set(dirtyIdsRef.current), []);

  const flushPendingRegions = useCallback(() => {
    clearIdleTimer();
    const regions = collectDirtyRegionsPayload();
    if (regions.length === 0) {
      return;
    }
    void patchRegionsRef.current({ regions }).catch((err) => {
      console.error("[HtmlContent] island autosave failed:", err);
    });
  }, [clearIdleTimer, collectDirtyRegionsPayload]);

  const resetAutosave = useCallback(
    (options?: { flush?: boolean }) => {
      if (options?.flush) {
        flushPendingRegions();
      } else {
        clearIdleTimer();
      }
      baselineByIdRef.current.clear();
      dirtyIdsRef.current.clear();
      saveInFlightRef.current = false;
      flushAfterFlightRef.current = false;
      resetAutosaveState();
    },
    [clearIdleTimer, flushPendingRegions, resetAutosaveState],
  );

  useEffect(() => {
    return () => {
      flushPendingRegions();
      resetAutosaveState();
    };
  }, [flushPendingRegions, resetAutosaveState]);

  return {
    getDirtyEditIds,
    handleIslandFocus,
    handleIslandInput,
    handleIslandFocusOut,
    flushDirtyRegions,
    resetAutosave,
  };
}
